import { useMemo } from "react"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import { PLACEHOLDERS } from "assets/images"
import { CopyButton } from "components/Common/CopyButton"
import { generateStockText } from "helpers/stock_helpers"
import formatMileage from "utils/formatMileage"

/**
 * Vehicle model/specs table cell.
 *
 * @param {object}  stock            - Vehicle stock record.
 * @param {boolean} showIdentifiers  - When true, also renders the linked
 *   stock ID and chassis number. Pages that already have a dedicated Stock ID
 *   column (e.g. the stock table) should leave this off to avoid duplication.
 * @param {boolean} showImage        - When true, renders the vehicle photo:
 *   the featured image if one is flagged, otherwise the first image.
 */
const DriveLinkAction = ({ value, onPreview }) => {
  if (!value) return null

  return (
    <div className="d-flex align-items-center gap-1 mt-1">
      {onPreview ? (
        <button
          type="button"
          className="btn btn-sm btn-soft-primary py-0 px-2"
          title="Preview Google Drive images"
          onClick={onPreview}
        >
          <i className="bx bx-images me-1"></i>
          Drive Images
        </button>
      ) : (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-sm btn-soft-primary py-0 px-2"
          title="View Google Drive link"
        >
          <i className="bx bx-link-external me-1"></i>
          Drive
        </a>
      )}
      <CopyButton
        text={value}
        variant="outline-secondary"
        className="py-0 px-1"
        title="Copy Google Drive link"
        successTitle="Drive link copied"
        toastCallback={type => {
          if (type === "success") {
            toast.success("Google Drive link copied to clipboard.")
          } else {
            toast.error("Failed to copy Google Drive link.")
          }
        }}
      />
    </div>
  )
}

const displayValue = value =>
  value === null || value === undefined || value === "" ? "-" : value

const relationName = relation => (relation && relation.name ? relation.name : "-")

const StockModelCell = ({
  stock,
  isMobile,
  toggleRow,
  isExpanded,
  rowId,
  showIdentifiers = false,
  showImage = false,
  onPreviewDriveImages,
}) => {
  const expanded = isExpanded(rowId)

  const textToCopy = useMemo(() => generateStockText(stock), [stock])
  const driveLink =
    (stock && stock.drive_link) || (stock && stock.google_drive_link) || ""

  // Featured image when flagged, else the first image; backend `primary_image`
  // applies the same rule, and the "photo coming soon" placeholder is the
  // final fallback when the vehicle has no images at all.
  const featuredImage = useMemo(() => {
    const images = stock && Array.isArray(stock.images) ? stock.images : []
    const primary = images.find(img => img && img.isPrimary)
    return (
      (primary && primary.url) ||
      (images[0] && images[0].url) ||
      (stock && stock.primary_image) ||
      PLACEHOLDERS.comingSoon
    )
  }, [stock])

  const content = (
    <div
      className="text-wrap"
      style={{
        maxWidth: "500px",
        minWidth: "8rem",
        whiteSpace: "normal",
        overflowWrap: "break-word",
      }}
    >
      <div className="d-flex flex-wrap gap-1">
        <div className="fw-bold">
          {relationName(stock && stock.brand)}{" "}
          {relationName(stock && stock.model)}{" "}
          {relationName(stock && stock.engine_type)}
        </div>

        <div className="d-flex gap-1">
          <CopyButton text={textToCopy} />
          {isMobile && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => toggleRow(rowId)}
            >
              {expanded ? "▼" : "▶"}
            </button>
          )}
        </div>
      </div>

      <DriveLinkAction
        value={driveLink}
        onPreview={
          onPreviewDriveImages ? () => onPreviewDriveImages(stock) : undefined
        }
      />

      {showIdentifiers && (
        <div className="small mt-1">
          <strong className="text-primary">
            <i className="bx bx-hash me-1" />
            <Link to={`/stock-list/${stock.stock_id}/view`}>
              {displayValue(stock.stock_id)}
            </Link>
          </strong>
          {stock.full_chassis ? (
            <>
              {" • "}
              <strong className="text-success">
                <i className="bx bx-barcode me-1" />
                <Link to={`/stock-list/${stock.full_chassis}/view`}>
                  {stock.full_chassis}
                </Link>
              </strong>
            </>
          ) : null}
        </div>
      )}

      {(expanded || !isMobile) && (
        <div className="text-muted d-block mt-1">
          Package: <span className="badge bg-light text-dark">{relationName(stock && stock.package)}</span>{" "}
          • Year: <span className="badge bg-light text-dark">{relationName(stock && stock.year)}</span>{" "}
          • Engine CC: <span className="badge bg-light text-dark">{relationName(stock && stock.engine_c_c)}</span>{" "}
          • Transmission: <span className="badge bg-light text-dark">{relationName(stock && stock.transmission)}</span>{" "}
          • Fuel: <span className="badge bg-light text-dark">{relationName(stock && stock.fuel_type)}</span>{" "}
          • Key: <span className="badge bg-light text-dark">{relationName(stock && stock.key_type)}</span>{" "}
          • Exterior: <span className="badge bg-light text-dark">{relationName(stock && stock.exterior_color)}</span>{" "}
          • Interior: <span className="badge bg-light text-dark">{relationName(stock && stock.interior_color)}</span>{" "}
          • Auction Grade: <span className="badge bg-warning">{relationName(stock && stock.auction_grade)}</span>{" "}
          • Mileage: <span className="badge bg-light text-dark">{formatMileage(stock.mileage)}</span>{" "}
          • Doors: <span className="badge bg-light text-dark">{displayValue(stock.doors)}</span>{" "}
          • Seats: <span className="badge bg-light text-dark">{displayValue(stock.seats)}</span>{" "}
          • Tire: <span className="badge bg-light text-dark">{displayValue(stock.tire_size)}</span>
        </div>
      )}
    </div>
  )

  if (!showImage) {
    return content
  }

  return (
    <div className="d-flex gap-2">
      <img
        src={featuredImage}
        alt="Vehicle"
        className="rounded flex-shrink-0"
        style={{ width: 84, height: 64, objectFit: "cover" }}
        onError={e => {
          e.currentTarget.src = PLACEHOLDERS.comingSoon
        }}
      />
      {content}
    </div>
  )
}

export default StockModelCell
